import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {throttle} from 'lodash';
import cx from 'classnames';

import QuestionCategories from './QuestionCategory';
import Link from '../../components/Link';
import Markdown, {H2} from '../../components/Markdown';
import MDX from '../../components/MDX';
import FAQSection from '../../components/FAQSection';
import FadeOut from '../../components/FadeOut';
import DocumentHeader from '../../components/DocumentHeader';
import {Article, Breadcrumb} from '../../components/LinkedData';
import {ChevronIcon} from '../../components/svg';
import TableOfContents, {generateTOC} from '../../components/TableOfContents';
import AuthorCard from '../../components/AuthorCard';
import ContentCard from '../../components/ContentCard';
import QuestionCard from '../../components/QuestionCard';
import SignupBox from '../../components/SignupBox';
import YouTubeVideo from '../../components/YouTubeVideo';
import {useAppContext, useAnalytics} from '../../hooks';
import {getHeaders} from './util';

const scrollIntoView = (id, offset = 100) => {
  const el = document.getElementById(id);
  if (el) window.scrollTo({left: 0, top: el.offsetTop - offset, behavior: 'smooth'});
};

const GuideMarkdown = (props) => (
  <Markdown
    className="prose lg:prose-lg prose-indigo"
    allowRaw
    {...props}
  />
);

const GuideMDX = (props) => (
  <MDX
    className="prose lg:prose-lg prose-indigo"
    allowRaw
    {...props}
  />
);

const Section = ({title, children}) => (
  <section className="mt-10">
    {title && (
      <span className="prose lg:prose-lg prose-indigo">
        <H2 className="font-bold text-lg lg:text-2xl text-gray-900 mb-4">{title}</H2>
      </span>
    )}
    {children}
  </section>
);

const GuidePage = ({
  slug, guide, lessonMapping, preview,
}) => {
  const {currentUser} = useAppContext();
  const {tracker} = useAnalytics();
  const headers = useMemo(() => getHeaders(guide), [guide]); // deprecated
  const [scrolled, setScrolled] = useState(false); // deprecated
  const [activeHeader, setActiveHeader] = useState(null); // deprecated
  const [toc, setTOC] = useState();
  const headerRef = useRef();

  useEffect(() => {
    if (window.location.hash) {
      scrollIntoView(window.location.hash.substring(1));
    }
  }, []);

  useEffect(() => {
    if (!guide) return;

    if (guide.content) setTOC(generateTOC());

    if (!preview) tracker.companyView({guide: guide.name});
  }, [guide]);

  useEffect(() => {
    // Note: Remove effect after migrating content.
    const listener = throttle(() => {
      if (!headerRef.current) return;
      const {top} = headerRef.current.getBoundingClientRect();

      if (!scrolled && top <= 0) {
        setScrolled(true);
      } else if (scrolled && top > 0) {
        setScrolled(false);
      }

      const active = headers.find((header) => {
        const el = document.getElementById(header.slug);
        const section = el ? el.parentElement : null;
        if (section) {
          const {top: sectionTop, bottom} = section.getBoundingClientRect();
          return sectionTop < 120 && bottom > 120; // TODO make constants here...
        }
        return false;
      });

      if (scrolled) {
        if (active && active !== activeHeader) {
          setActiveHeader(active.slug);
        }
      } else {
        setActiveHeader(null);
      }
    }, 50);

    window.addEventListener('scroll', listener);

    return () => window.removeEventListener('scroll', listener);
  });

  const companyName = guide.company.name;

  const description = `
    Prepare for the ${companyName} ${guide.roleName || ''} interview with an inside look at the
    interview process and sample questions. Learn how to get a ${guide.roleName || ''} job
    at ${companyName} with essential tips from past interviewers and hiring managers.
  `;

  // Note: Remove after migrating content to new format
  const onHeaderClick = (e, id) => {
    e.preventDefault();
    e.target.parentElement.scrollTo({left: e.target.offsetLeft - 10, behavior: 'smooth'});
    scrollIntoView(id);
  };

  const currentYear = new Date().getFullYear();

  return (
    <div className="bg-white resize-iframe">
      {/* Metadata */}
      <DocumentHeader
        title={`${guide.name} | Sample Questions (${currentYear})`}
        metaDescription={description}
        socialDescription={description}
      />
      <Breadcrumb items={[
        {name: 'Guides', url: 'https://www.tryexponent.com/guides'},
        {name: companyName, url: `https://www.tryexponent.com/guides/${slug}`},
      ]}
      />
      <Article
        headline={guide.name}
        image={[`${guide.company.logo}?w=200&h=200`]}
        datePublished={guide.createdAt}
        dateModified={guide.updatedAt}
      />

      {/* Header section */}
      <div className="grid grid-cols-6 max-w-7xl mx-auto pt-8">
        <div className="px-4 md:px-16 col-span-6 lg:col-start-2 lg:col-span-4">
          <img
            src={`${guide.company.logo}?w=128&h=128`}
            alt={`${companyName} logo`}
            className="h-8 w-8 md:h-16 md:w-16 mb-4 object-contain"
          />
          <div className="flex items-center">
            <span className="flex items-center text-base font-semibold text-gray-400 space-x-1">
              <Link href="/guides">All Guides</Link>
              <ChevronIcon className="transform -rotate-90" aria-hidden="true" />
              <span>{companyName}</span>
            </span>
          </div>
          <h1 className="text-2xl md:text-3xl font-extrabold text-black mt-4 mb-4" style={{fontFamily: 'Sora'}}>{guide.name}</h1>

          {guide.author && (
            <AuthorCard author={guide.author} className="mt-4 mb-6" />
          )}

          <span className="block text-lg text-gray-500 font-medium mb-2">
            {`Learn how to prepare for the ${companyName} ${guide.roleName || ''} interview and get a job at ${companyName} with this in-depth guide.`}
          </span>
        </div>
      </div>

      {/* Sticky navigation header - to be deprecated */}
      {!guide.content && (
        <div
          ref={headerRef}
          className={cx('flex sticky top-0 mb-4 bg-white z-10 overflow-x-scroll', {'shadow-md': scrolled})}
        >
          <FadeOut className="mx-auto max-w-4xl w-full" right>
            <nav className={cx('scrollbar-hidden overflow-x-scroll mx-0 md:mx-4', {'border-b border-gray-200': !scrolled})}>
              <ol className="flex space-x-4 mx-4 md:mx-0">
                <span className="py-4 font-semibold text-base text-gray-400 whitespace-nowrap">Jump to...</span>
                {headers.map((header) => (
                  <li className="last:pr-6">
                    <a
                      className={cx(
                        'whitespace-nowrap block text-base py-4 border-b-3 px-0 font-semibold', {
                          'border-indigo text-indigo': header.slug === activeHeader,
                          'border-transparent text-gray-600': header.slug !== activeHeader,
                        },
                      )}
                      href={header.slug ? `#${header.slug}` : ''}
                      onClick={(e) => onHeaderClick(e, header.slug)}
                      key={header.slug}
                    >
                      {header.name}
                    </a>
                  </li>
                ))}
              </ol>
            </nav>
          </FadeOut>
        </div>
      )}

      {/* Main content */}
      <div className="grid grid-cols-6 max-w-7xl mx-auto pb-20">
        {/* Table of contents */}
        {toc?.length > 0 && (
          <div className="hidden lg:block col-span-1">
            <div className="sticky top-20">
              <TableOfContents sections={toc} />
            </div>
          </div>
        )}

        {toc?.length > 0 && (
          <div className="lg:hidden col-span-6 px-4 md:px-16">
            <TableOfContents sections={toc} mobile />
          </div>
        )}

        {/* Body */}
        <div className="px-4 md:px-16 col-span-6 lg:col-start-2 lg:col-span-4">

          {guide.video && (
            <div className="rounded overflow-hidden max-w-5xl mx-auto mt-6 mb-4">
              <YouTubeVideo
                videoID={guide.video}
                onView={() => tracker.guideViewVideo({guide: guide.name})}
                onPlay={() => tracker.guidePlayVideo({guide: guide.name})}
              />
            </div>
          )}

          {/* New guide content format */}
          {guide.content && (
            <GuideMDX onRender={() => setTOC(generateTOC())}>
              {guide.content}
            </GuideMDX>
          )}

          {/* Older content format in multiple sections */}
          {!guide.content && (
            <div>
              <Section>
                <div id="overview" />
                <GuideMarkdown>{guide.intro}</GuideMarkdown>
              </Section>
              {guide.stages && (
                <Section title="Interview Process">
                  <GuideMarkdown>{guide.stages}</GuideMarkdown>
                </Section>
              )}
              {guide.questionTypes?.length > 0 && (
                <Section title="Sample Interview Questions">
                  <QuestionCategories
                    categories={guide.questionTypes}
                    description={guide.questionDescription}
                    lessonMapping={lessonMapping}
                  />
                </Section>
              )}
              {guide.evaluation && (
                <Section title="Hiring Decision">
                  <GuideMarkdown>{guide.evaluation}</GuideMarkdown>
                </Section>
              )}
              {guide.additionalContent?.length > 0 && (
                guide.additionalContent.map((content) => (
                  <Section title={content.name}>
                    <GuideMarkdown>{content.description}</GuideMarkdown>
                  </Section>
                ))
              )}
            </div>
          )}

          {/* FAQ Section */}
          {guide.faq?.length > 0 && (
            <Section title="FAQ">
              <FAQSection items={guide.faq} includeMetadata />
            </Section>
          )}
        </div>

        {/* Dynamic CTAs */}
        <div className="hidden lg:block col-span-1 mt-2">
          <div className="sticky top-20">
            <QuestionCard
              tag={{id: guide.roleTag, name: guide.roleName, type: 'role'}}
              trackerEvent={() => tracker.guideClickCard({type: 'questions', tag: guide.roleTag})}
              urlParams={{src: 'guide'}}
            />
            <ContentCard
              tag={{id: guide.roleTag, name: guide.roleName, type: 'role'}}
              trackerEvent={() => tracker.guideClickCard({type: 'courses', tag: guide.roleTag})}
              urlParams={{src: 'guide'}}
            />
          </div>
        </div>

        {!currentUser && (
          <div className="col-span-full flex justify-center">
            <div className="max-w-xl container">
              <SignupBox
                role={guide.roleName}
                href="/signup?src=guide"
                onClick={() => tracker.guideClickSignupCta({guide: guide.name, tag: guide.roleTag})}
              />
            </div>
          </div>
        )}

        {/* Sidebar module */}
        {/* Comment out for now to monitor CTA performance */}
        {/* {!shouldHideCoaching(currentUser) && <RecommendedCoachSection guide={guide} />} */}
      </div>
    </div>
  );
};

export default GuidePage;
